import React, { useEffect } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { CircularProgress, ThemeProvider } from '@material-ui/core';
import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react';

import { getTheme } from '@/utils/productHelpers';
import { getSupabaseClient } from '@/utils/supabaseClient';
import {
  REDIRECT_TO_SESSION_KEY,
  LOGIN_ORG_SLUG_PLACEHOLDER,
} from '@/utils/constants';
// eslint-disable-next-line max-len
import { appSuppliedRedirectTo } from '@/pages/PreOnboarding/steps/ActivateSleepio/hooks/usePostAuthRedirectTo';
import { buildRedirectParam, isAllowedAppHandoffTarget } from '@/utils/urlHelpers';
import { applyHandoffPayload } from '@/utils/handoffPayload';
import { logError } from '@/utils/logError';
import { ActiveFeatureFlag, isBooleanFeatureFlagEnabled } from '@/hooks/usePostHogFeatureFlags';
import { QUERY_DISTINCT_ID_WITH_EMAIL } from '@/apollo/queries';
import { linkPostHogIdentity } from '@/utils/posthog';

const SleepioEmailCallback: React.FC = () => {
  const history = useHistory();
  const { lob } = useParams<{ lob: string }>();
  const { search } = useLocation();
  const posthog = usePostHog();
  const [getDistinctId] = useLazyQuery(QUERY_DISTINCT_ID_WITH_EMAIL, { fetchPolicy: 'network-only' });
  const supabaseAuthEnabled = isBooleanFeatureFlagEnabled(
    useFeatureFlagVariantKey(ActiveFeatureFlag.CAP_USE_SUPABASE_AUTH),
  );

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const params = new URLSearchParams(search);

        // Restores `redirectTo` if the caller wrapped it in `hp`. See handoffPayload.ts.
        applyHandoffPayload(params);
        const effectiveSearch = params.toString();

        const redirectToParam = params.get('redirectTo');
        // Session storage is per-tab, so a plain scheme check isn't enough here — the
        // resolver that reads this key later only honours allowlisted handoff targets.
        if (redirectToParam && isAllowedAppHandoffTarget(redirectToParam)) {
          try {
            sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectToParam);
          } catch {
            // A browser with storage locked down throws here; this app has no error
            // boundary, so swallow it rather than blank the screen mid-flow.
          }
        }

        await new Promise((resolve) => { setTimeout(resolve, 1500); });

        const { data: { session } } = await getSupabaseClient().auth.getSession();

        if (!session) {
          history.replace('/activate/sleepio/access-code', { error: 'email_verification_failed' });
          return;
        }

        const phDistinctId = session.user?.user_metadata?.phDistinctId;
        if (typeof phDistinctId === 'string' && phDistinctId) {
          posthog?.identify(phDistinctId);
        }

        if (supabaseAuthEnabled === null) { return; }

        if (!supabaseAuthEnabled) {
          history.replace('/activate/sleepio/employers');
          return;
        }

        // `flow=login` names which VAF endpoint minted this link — /api/auth/login sets
        // it, activate/email's buildRedirectTo doesn't. Reading it here is exact; nothing
        // about the session has to be guessed.
        const isReturningUser = params.get('flow') === 'login';

        // Sign-up links carry org_slug; login links don't (no LOB/org context at login).
        // Only login gets a filler value — its route doesn't read the org, unlike
        // sign-up's, which passes it on to Cortex enrollment.
        const orgSlugParam = params.get('org_slug');
        const orgSlug = orgSlugParam || (isReturningUser ? LOGIN_ORG_SLUG_PLACEHOLDER : '');
        const intent = isReturningUser ? 'login' : 'sign-up';

        // A sign-up link should always carry an org; an empty segment here matches no
        // route and drops the member on an unrelated screen, so log it rather than let
        // it fail silently.
        if (!orgSlugParam && !isReturningUser) {
          logError('[SleepioEmailCallback]', 'sign-up callback has no org_slug; route will not match', undefined, {
            level: 'warning',
          });
        }

        // `appSuppliedRedirectTo`: absent stays absent. Substituting a default here would
        // write it onto the next URL as a real address, and every screen downstream would
        // read it back as genuinely supplied.
        const redirectParam = buildRedirectParam(appSuppliedRedirectTo({ search: effectiveSearch }));

        const email = session.user?.email;
        if (email) {
          // Fire-and-forget: React Router navigation keeps the Apollo client alive,
          // so the in-flight query completes after the component unmounts.
          linkPostHogIdentity(getDistinctId, posthog, email, '[SleepioEmailCallback]');
        }

        history.replace(`/activate/sleepio-canopy/${lob}/${orgSlug}/${intent}/email/callback${redirectParam}`);
      } catch (err) {
        logError('[SleepioEmailCallback]', 'Unexpected error', err);
        history.replace('/activate/sleepio/access-code', { error: 'email_verification_failed' });
      }
    };

    handleCallback();
  }, [history, lob, search, supabaseAuthEnabled, getDistinctId, posthog]);

  return (
    <ThemeProvider theme={getTheme('sleepio')}>
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
      }}
      >
        <CircularProgress color="primary" />
      </div>
    </ThemeProvider>
  );
};

export default SleepioEmailCallback;
